// Copyright 2025, University of Maryland, All Rights Reserved
import { AxiosInstance } from 'axios'
import type {
  WorkspaceCostPolicy,
  WorkspaceCostSnapshot,
  YearlyCostsResponse,
} from '../types'

export type CostLevel = 'app' | 'org' | 'workspace';

export const createCostsApi = (api_instance: AxiosInstance) => ({
  getYearlyCosts: async (
    app_id: string,
    year: number,
    organization_id: string = 'demo-org',
    workspace_id: string = 'demo-workspace',
    level: CostLevel = 'app'
  ): Promise<YearlyCostsResponse> => {
    console.log('🔍 Fetching yearly costs for app:', app_id, 'year:', year, 'org:', organization_id, 'workspace:', workspace_id, 'level:', level)
    
    let endpoint = '';
    
    // Create the endpoint based on the requested level using the exact route patterns from the backend
    switch (level) {
      case 'app':
        // App-level costs (global, without org/workspace)
        endpoint = `/costs/rollups/${year}`;
        break;
      case 'org':
        // Organization-level costs (without specific workspace)
        endpoint = `/org/${organization_id}/costs/rollups/${year}`;
        break;
      case 'workspace':
        // Workspace-level costs (specific to both org and workspace)
        endpoint = `/org/${organization_id}/ws/${workspace_id}/costs/rollups/${year}`;
        break;
      default:
        // Default to workspace-level if no valid level is provided
        endpoint = `/org/${organization_id}/ws/${workspace_id}/costs/rollups/${year}`;
    }
    
    const response = await api_instance.get<YearlyCostsResponse>(endpoint);

    console.log('✅ Fetched yearly costs:', response.data)
    return response.data
  },

  getWorkspaceCostPolicy: async (
    organization_id: string,
    workspace_id: string,
  ): Promise<WorkspaceCostSnapshot> => {
    const response = await api_instance.get<WorkspaceCostSnapshot>(
      `/org/${organization_id}/ws/${workspace_id}/costs/policy/monthly`,
    )
    return response.data
  },

  replaceWorkspaceCostPolicy: async (
    organization_id: string,
    workspace_id: string,
    policy: WorkspaceCostPolicy,
  ): Promise<WorkspaceCostSnapshot> => {
    const response = await api_instance.put<WorkspaceCostSnapshot>(
      `/org/${organization_id}/ws/${workspace_id}/costs/policy/monthly`,
      policy,
    )
    return response.data
  },
})
